import {ApiConfig, OnboardingStatus} from "src/components/types";
import {CLIENT_TYPE, INFRA_DEPLOYMENT_TYPE, REGION} from "src/constants";

export async function submitOnboardingRequest({ jsonOutput, token, environment }: {
    jsonOutput: string;
    token: string | null;
    environment: string | undefined;
}): Promise<string> {
    const response = await fetch(`https://ds2apionboarding-${environment}.iad.corp.amazon.com/onboardingRequests`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
        body: jsonOutput,
    });

    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status} body: ${response.body}`);
    }

    const data = await response.json();
    return data.pollingStatusUrl;
}

export function pollOnboardingStatus({
                                         pollingUrl,
                                         token,
                                         onUpdate,
                                         onComplete,
                                         onError,
                                     }: {
    pollingUrl: string;
    token: string | null;
    onUpdate: (status: string) => void;
    onComplete: (finalStatus: string) => void;
    onError: (error: unknown) => void;
}) {
    const pollInterval = setInterval(async () => {
        let shouldClear = false;
        try {
            const response = await fetch(pollingUrl, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                shouldClear = true;
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const { status } = await response.json();
            onUpdate(status);

            if (status === OnboardingStatus.ACCEPTED) {
                return;
            }
            if (status === OnboardingStatus.SUCCEEDED || status === OnboardingStatus.FAILED) {
                shouldClear = true;
                onComplete(status);
            }
        } catch (error) {
            shouldClear = true;
            onError(error);
        } finally {
            if (shouldClear) {
                clearInterval(pollInterval);
            }
        }
    }, 5000);
}

export function buildOnboardingJson(formData: ApiConfig, CLIENT_AUTHENTICATION: boolean): string {
    const apiJson = {
        environment: formData.environment?.value || "",
        region: REGION,
        serviceName: (formData.superStar?.value || "").split("/").filter(Boolean)[1] || (formData.superStar?.value || ""),
        superstarService: formData.superStar?.value || "",
        aaaIdentifier: formData.aaaIdentifier,
        infraDeploymentType: INFRA_DEPLOYMENT_TYPE,
        backendBaseUrl: (formData.superStar?.description || "") + '/' + formData.resource,
        corsEnabled: formData.corsEnabled === "true",
        corsHeaders: formData.corsEnabled === "true"
            ? formData.corsHeaders.filter(header => header.trim() !== "").join(", ")
            : "",
        resource: formData.resource,
        action: formData.customAction === "" ? formData.httpMethod?.value : formData.customAction,
        resourceUrnTemplate: formData.resourceUrnTemplate,
        clientAuthConfig: {
            clientType: CLIENT_TYPE,
            isClientAuthenticationEnabled: CLIENT_AUTHENTICATION,
            isClientAuthorizationEnabled: formData.clientAuthorization === "true",
            isRequestSigningEnabled: true,
        },
        tags: [3],
        apiSpec: buildSwaggerSpec(formData),  // <-- Use helper here
    };

    return JSON.stringify(apiJson, null, 2);
}

function buildSwaggerSpec(formData: ApiConfig) {
    return {
        swagger: "2.0",
        info: {
            description: (formData.superStar?.value || "").split("/").filter(Boolean)[1] || (formData.superStar?.value || ""),
            version: "2024-06-10T13:11:33Z",
            title: " "
        },
        schemes: ["HTTPS"],
        paths: {
            [`/${formData.resource}`]: {
                [formData.httpMethod!.value!]: {
                    parameters: [
                        ...formData.requiredParameters.map(param => ({
                            name: param.name,
                            in: param.type,
                            required: true,
                            type: "string",
                        })),
                        ...formData.optionalParameters.map(param => ({
                            name: param.name,
                            in: param.type,
                            required: false,
                            type: "string",
                        })),
                    ],
                    responses: {
                        "200": {
                            description: "200 response",
                        },
                    },
                },
            },
        },
    };
}
